import { create } from 'zustand';
import { User } from '../types';
import { authApi, SignUpData, SignInData } from '../api/auth';
import { uploadImage } from '../api/storage';
import { redeemInviteCode as apiRedeemInviteCode, RedeemInviteCodeResult } from '../api/invites';
import { getErrorMessage } from '../utils/errorHandler';
import { withLoading } from './utils/withLoading';
import { useOnboardingStore } from './onboardingStore';
import { useUIStore } from './uiStore';
import { useStructuresStore } from './structuresStore';
import { usePostsStore } from './postsStore';
import { useNotificationsStore } from './notificationsStore';
import { useChecklistStore } from './checklistStore';
import { useChecklistThreadsStore } from './checklistThreadsStore';
import { useAgentClientsStore } from './agentClientsStore';
import { usePlanEventsStore } from './planEventsStore';
import { flushDeferredCommits } from '../utils/deferredCommit';

interface AuthState {
  user: User | null;
  // The session's `moderator` custom claim (curation: delete any post or
  // comment). Read off the ID token on each auth-state change — it lives on
  // the token, not the user doc, so it's session state here rather than a
  // User field.
  isModerator: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
  returnTo: string | null;

  initialize: () => (() => void) | void;
  signUp: (data: SignUpData) => Promise<void>;
  signIn: (data: SignInData) => Promise<void>;
  signInAsScout: () => Promise<void>;
  /**
   * Web-only Google popup sign-in/sign-up. Returns false (rather than
   * throwing) when the user dismisses the popup, so callers can no-op
   * instead of surfacing an error banner for a routine cancel. Doesn't touch
   * shared `isLoading` — surfaces track their own busy state so the rest of
   * the screen doesn't lock up while the popup is open.
   */
  /** `extraRoles` carries the audience from a /start/<audience> intro page. */
  signInWithGoogle: (extraRoles?: import('../types').UserRole[]) => Promise<boolean>;
  upgradeAccount: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }) => Promise<void>;
  /**
   * Web-only Google-popup counterpart to upgradeAccount. Returns a status
   * instead of throwing for the two non-fatal outcomes ('cancelled' and
   * 'account-exists') so the sheet can render its own follow-up UI.
   */
  upgradeAccountWithGoogle: () => Promise<'upgraded' | 'cancelled' | 'account-exists'>;
  /**
   * Sign into the account stashed by upgradeAccountWithGoogle's
   * 'account-exists' outcome. Mirrors signIn's isLoading handling (stays
   * true through completion; the auth listener flips it off).
   */
  signInWithPendingGoogleCredential: () => Promise<void>;
  signOut: () => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  updateProfile: (
    data: Partial<
      Pick<
        User,
        | 'firstName'
        | 'lastName'
        | 'emailNotificationsEnabled'
        | 'avatarUrl'
        | 'avatarPath'
        | 'notificationPreferences'
      >
    >
  ) => Promise<void>;
  /**
   * Upload a new avatar image from a local URI, then update the user profile
   * with the resulting URL and storage path. Keeps screens away from the
   * storage api layer.
   */
  uploadAvatar: (localUri: string) => Promise<void>;
  /**
   * Redeem an agent invite code for the signed-in user and optimistically
   * update the in-memory user with the resulting agent linkage. Returns null
   * when the code is not recognized; throws on other failures (already linked,
   * anonymous account, network) so the screen can surface a specific message.
   */
  redeemInviteCode: (code: string) => Promise<RedeemInviteCodeResult | null>;
  /**
   * Persist the user's self-declared role set and optimistically update
   * the in-memory user so callers (profile, Tour REDI highlights) see
   * the change immediately. Best-effort by design — a roles save failure
   * shouldn't surface as a hard error; authApi.updateUserRoles already
   * logs and swallows internally.
   */
  updateRoles: (roles: import('../types').UserRole[]) => Promise<void>;
  deleteAccount: () => Promise<void>;
  setReturnTo: (path: string | null) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isModerator: false,
  isLoading: false,
  isInitialized: false,
  error: null,
  returnTo: null,

  initialize: () => {
    const unsubscribe = authApi.onAuthStateChanged(async ({ user, error }) => {
      if (user) {
        // The user doc is already loaded by onAuthStateChanged before this
        // callback fires, and hydrate(user) overwrites every onboarding
        // field — the old AsyncStorage pre-hydrate (readOnboardingCache)
        // was clobbered synchronously and only cost a blocking read on the
        // startup critical path, so it was removed.
        useOnboardingStore.getState().hydrate(user);
        // Migration backstop: existing users who already completed the old
        // forced-onboarding flow have effectively "seen" the system overview.
        // Flip the tour-seen flag so they don't get the avatar's pulsing dot.
        // New signups start with hasCompletedOnboarding unset, so they still
        // get the pulsing dot until they open the tour.
        if (user.hasCompletedOnboarding && !useUIStore.getState().hasSeenTour) {
          useUIStore.getState().markTourSeen();
        }
        set({ user, error: null, isInitialized: true, isLoading: false });
        // After the user lands: the claim read is non-blocking on purpose so
        // a slow token refresh can't delay startup.
        void authApi.getModeratorClaim().then((isModerator) => set({ isModerator }));
      } else {
        useOnboardingStore.getState().hydrate(null);
        // `error` is only set when a live session exists but its user doc is
        // unreadable/invalid — surface it rather than a silent signed-out UI.
        // Omit the key otherwise so a normal sign-out doesn't clobber an error
        // a prior callback just set (e.g. the forced re-emit chain).
        set({
          user: null,
          isModerator: false,
          isInitialized: true,
          isLoading: false,
          ...(error ? { error } : {}),
        });
      }
    });

    return unsubscribe;
  },

  signUp: async (data: SignUpData) => {
    await withLoading(set, 'isLoading', async () => {
      const user = await authApi.signUp(data);
      set({ user });
    });
  },

  signInAsScout: async () => {
    set({ isLoading: true, error: null });
    try {
      const user = await authApi.signInAsScout();
      set({ user });
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false });
      throw error;
    }
  },

  upgradeAccount: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const user = await authApi.upgradeFromAnonymous(data);
      // Hydrate immediately so the route guard sees hasCompletedOnboarding:true
      // before the onAuthStateChanged listener catches up. Without this, the
      // guard briefly redirects the just-upgraded user to /onboarding/welcome.
      useOnboardingStore.getState().hydrate(user);
      set({ user, isLoading: false });
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false });
      throw error;
    }
  },

  upgradeAccountWithGoogle: async () => {
    set({ error: null });
    try {
      const result = await authApi.upgradeFromAnonymousWithGoogle();
      if (result.status === 'upgraded') {
        // Same reasoning as upgradeAccount above: hydrate before the
        // onAuthStateChanged listener catches up.
        useOnboardingStore.getState().hydrate(result.user);
        set({ user: result.user });
      }
      return result.status;
    } catch (error) {
      set({ error: getErrorMessage(error) });
      throw error;
    }
  },

  signInWithPendingGoogleCredential: async () => {
    // Don't set isLoading to false on success — mirrors signIn: the auth
    // state listener updates the user and the route guard redirects.
    set({ isLoading: true, error: null });
    try {
      await authApi.signInWithPendingGoogleCredential();
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false });
      throw error;
    }
  },

  signIn: async (data: SignInData) => {
    // Don't set isLoading to false on success — the auth state listener will
    // update the user and trigger redirect. Keeping isLoading true prevents
    // a flash of the sign-in screen before the redirect.
    set({ isLoading: true, error: null });
    try {
      await authApi.signIn(data);
    } catch (error) {
      set({ error: getErrorMessage(error), isLoading: false });
      throw error;
    }
  },

  signInWithGoogle: async (extraRoles) => {
    set({ error: null });
    try {
      const user = await authApi.signInWithGoogle(extraRoles);
      if (!user) return false;
      // onAuthStateChanged emits { user: null } here because the doc didn't
      // exist yet when the listener's getDoc ran (see the doc-absent case
      // in authApi.onAuthStateChanged) — the create flow owns the result
      // directly, same as signUp.
      useOnboardingStore.getState().hydrate(user);
      set({ user });
      return true;
    } catch (error) {
      set({ error: getErrorMessage(error) });
      throw error;
    }
  },

  signOut: async () => {
    await withLoading(set, 'isLoading', async () => {
      // Run any deferred write (e.g. a "Remove party" undo toast still in
      // its window — src/utils/deferredCommit.ts) before the credentials
      // that write needs go away, and before uiStore.reset() below drops
      // the toast that offered Undo for it.
      await flushDeferredCommits();
      await authApi.signOut();
      set({ user: null });
      // Session teardown: each store owns its reset() — see those
      // implementations for the why (uiStore's covers the tab-bar
      // refcount leak; structuresStore's the stale selection).
      useUIStore.getState().reset();
      useStructuresStore.getState().reset();
      // Drop cached content tied to the old identity: posts (including
      // insurance-visibility docs an agent could see), like state, and the
      // previous user's notifications. The feed refetches on focus, but
      // until then this memory must not leak into the next session.
      usePostsStore.getState().reset();
      useNotificationsStore.getState().reset();
      // Checklist threads hold live onSnapshot listeners per expanded item;
      // without this they keep streaming (and billing) into the next
      // session. The checklist + agent-client stores hold the previous
      // identity's progress and client PII (reportRecipients).
      useChecklistThreadsStore.getState().unsubscribeAll();
      useChecklistStore.getState().reset();
      useAgentClientsStore.getState().reset();
      // Event and sign-up listeners hold the owner's attendee emails.
      usePlanEventsStore.getState().reset();
      // Wipe Workbox-owned caches so the next person to use this
      // browser profile can't pull the previous user's Firestore
      // responses out of the service worker cache. No-op on native and
      // when the SW isn't registered.
      if (typeof globalThis !== 'undefined' && 'caches' in globalThis) {
        try {
          const names = await globalThis.caches.keys();
          await Promise.all(names.map((n) => globalThis.caches.delete(n)));
        } catch {
          // Non-fatal: sign-out itself already succeeded.
        }
      }
    });
  },

  sendPasswordResetEmail: async (email: string) => {
    await withLoading(set, 'isLoading', async () => {
      await authApi.sendPasswordResetEmail(email);
    });
  },

  updateProfile: async (data) => {
    const { user } = get();
    if (!user) throw new Error('Not authenticated');

    await withLoading(set, 'isLoading', async () => {
      await authApi.updateProfile(user.id, data);
      set({ user: { ...user, ...data } });
    });
  },

  uploadAvatar: async (localUri: string) => {
    const { user } = get();
    if (!user) throw new Error('Not authenticated');
    const { downloadUrl, storagePath } = await uploadImage(
      localUri,
      `avatars/${user.id}/avatar.jpg`,
      'avatar'
    );
    await get().updateProfile({ avatarUrl: downloadUrl, avatarPath: storagePath });
  },

  redeemInviteCode: async (code: string) => {
    const result = await apiRedeemInviteCode(code);
    if (result) {
      // Reflect the linkage on the in-memory user so the claim flow
      // pre-selects the insurer without waiting for an auth refresh.
      set((s) =>
        s.user
          ? {
              user: {
                ...s.user,
                invitedByAgentId: result.agentId,
                defaultInsurer: result.insurerName ?? undefined,
              },
            }
          : {}
      );
    }
    return result;
  },

  updateRoles: async (roles) => {
    const { user } = get();
    if (!user) return;
    await authApi.updateUserRoles(user.id, { roles });
    // Optimistically reflect on the in-memory user so callers (profile,
    // Tour REDI highlights) update without waiting for an auth refresh.
    set((s) => (s.user ? { user: { ...s.user, roles } } : {}));
  },

  deleteAccount: async () => {
    const { user } = get();
    if (!user) throw new Error('Not authenticated');

    await withLoading(set, 'isLoading', async () => {
      await authApi.deleteAccount(user.id);
      set({ user: null });
    });
  },

  setReturnTo: (path) => {
    set({ returnTo: path });
  },

  clearError: () => {
    set({ error: null });
  },
}));
